import { BooleanInput } from '@angular/cdk/coercion';
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    inject,
    Input,
    OnDestroy,
    OnInit,
    ViewEncapsulation,
} from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { Router } from '@angular/router';

import { AuthService, PermissionService, Tenant, TenantService, User } from '@hta/auth';
import { Subject, takeUntil } from 'rxjs';

@Component({
    selector: 'user',
    templateUrl: './user.component.html',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    exportAs: 'user',
    imports: [
        MatButtonModule,
        MatMenuModule,
        MatIconModule,
        MatDividerModule,
        MatProgressSpinnerModule,
    ],
})
export class UserComponent implements OnInit, OnDestroy {
    /* eslint-disable @typescript-eslint/naming-convention */
    static ngAcceptInputType_showAvatar: BooleanInput;
    /* eslint-enable @typescript-eslint/naming-convention */

    @Input() showAvatar: boolean = true;
    user: User;
    /** Lista de corporativos del usuario. */
    tenants: Tenant[] = [];
    /** ID del corporativo actualmente activo (null si ninguno). */
    currentTenantId: number | null = null;
    /** Nombre del corporativo actualmente activo. */
    currentTenantName: string | null = null;
    /** True mientras se procesa un cambio de corporativo. */
    switchingTenant = false;
    private readonly authService = inject(AuthService);
    private readonly tenantService = inject(TenantService);
    private readonly permissionService = inject(PermissionService);

    /** Observables derivados de signals en field initializers (contexto de inyección válido). */
    private readonly _currentTenant$ = toObservable(this.tenantService.currentTenant);
    private readonly _tenants$ = toObservable(this.tenantService.tenants);
    private _unsubscribeAll: Subject<any> = new Subject<any>();

    /**
     * Constructor
     */
    constructor(
        private _changeDetectorRef: ChangeDetectorRef,
        private _router: Router,
    ) {}

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void {
        // Subscribe to user changes
        this.authService.getUser()
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((user: User) => {
                this.user = user;

                // Mark for check
                this._changeDetectorRef.markForCheck();
            });

        // Subscribe to current tenant changes (signal → observable)
        this._currentTenant$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((current) => {
                this.currentTenantId = current?.tenantId ?? null;
                this.currentTenantName = current?.tenantName ?? null;
                this._changeDetectorRef.markForCheck();
            });

        // Subscribe to tenants list (signal → observable)
        this._tenants$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((tenants) => {
                this.tenants = tenants ?? [];
                this._changeDetectorRef.markForCheck();
            });

        // Cargar lista de corporativos del usuario.
        // Si ya están en cache, no se vuelve a pedir al backend.
        this.tenantService.getTenants().catch(() => {
            // Silenciar errores: el menú se degrada a "sin corporativos" sin romper la app.
        });
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void {
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Indica si el usuario tiene más de un corporativo (mostrar selector).
     */
    get hasMultipleTenants(): boolean {
        return this.tenants.length > 1;
    }

    /**
     * Indica si el tenant dado es el actualmente activo.
     */
    isCurrentTenant(tenantId: number): boolean {
        return this.currentTenantId === tenantId;
    }

    /**
     * Cambia el corporativo activo. Tras cambiar:
     *  1. Llama POST /api/auth/select-tenant/{id} (crea cookie de sesión).
     *  2. Invalida la caché de permisos del frontend.
     *  3. Recarga la página para que todos los componentes lean el nuevo tenant.
     */
    switchTenant(tenantId: number): void {
        if (this.switchingTenant || this.isCurrentTenant(tenantId)) {
            return;
        }
        this.switchingTenant = true;
        this.tenantService.selectTenant(tenantId).subscribe({
            next: () => {
                this.permissionService.clearCache();
                // Recarga dura: garantiza que cualquier componente cacheado
                // (permisos, sucursales, datos del tenant) se reconstruya desde
                // el backend bajo el nuevo contexto de tenant.
                window.location.assign('/');
            },
            error: () => {
                this.switchingTenant = false;
                this._changeDetectorRef.markForCheck();
            }
        });
    }

    /**
     * Update the user status
     *
     * @param status
     */
    updateUserStatus(status: string): void {
        // Return if user is not available
        if (!this.user) {
            return;
        }

        // Update the user
       /* this._userService
            .update({
                ...this.user,
                status,
            })
            .subscribe();*/
    }

    /**
     * Sign out
     */
    signOut(): void {
        this._router.navigate(['/sign-out']);
    }
}
